import { useEffect, useState } from "react";
import { Star, X, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function TestimonialPrompt() {
  const [show, setShow] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [quote, setQuote] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("fv_testimonial_checked")) return;
    sessionStorage.setItem("fv_testimonial_checked", "1");
    api.get("/testimonials/eligibility")
      .then((r) => { if (r.data?.should_prompt) setShow(true); })
      .catch(() => {});
  }, []);

  const dismiss = async () => {
    setShow(false);
    try { await api.post("/testimonials/dismiss"); } catch (e) { /* ignore */ }
  };

  const submit = async () => {
    if (!rating) { toast.error("Please choose a star rating"); return; }
    if (quote.trim().length < 10) { toast.error("Please share a little more (10+ characters)"); return; }
    setBusy(true);
    try {
      await api.post("/testimonials/submit", { rating, quote, company });
      toast.success("Thank you! Your story is pending review. 🙏");
      setShow(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed z-50 bottom-4 right-4 left-4 sm:left-auto sm:w-[400px] rounded-2xl border border-[#D4AF37]/30 bg-[#121214] shadow-2xl p-5"
          data-testid="testimonial-prompt">
          <button onClick={dismiss} data-testid="testimonial-dismiss"
            className="absolute top-3 right-3 text-[#71717A] hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1 text-[#D4AF37]"><Quote className="w-4 h-4" />
            <span className="text-[11px] tracking-[0.15em] uppercase">Enjoying Fairview?</span></div>
          <h3 className="text-white font-heading text-lg mb-1">Share your story</h3>
          <p className="text-xs text-[#A1A1AA] mb-3">You've made real progress — would you tell other founders how it's going? It only takes a moment.</p>

          <div className="flex gap-1 mb-3" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`testimonial-star-${n}`}
                onMouseEnter={() => setHover(n)} onClick={() => setRating(n)}
                className="p-0.5">
                <Star className={`w-6 h-6 transition-colors ${(hover || rating) >= n ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/20"}`} />
              </button>
            ))}
          </div>

          <Textarea value={quote} onChange={(e) => setQuote(e.target.value)} data-testid="testimonial-quote"
            placeholder="What's Fairview Systems helped you achieve?"
            className="bg-[#0d0d0f] border-white/10 text-white text-sm mb-2 min-h-[80px]" />
          <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="testimonial-company"
            placeholder="Company (optional)" className="bg-[#0d0d0f] border-white/10 text-white text-sm mb-3" />

          <div className="flex gap-2">
            <Button onClick={submit} disabled={busy} data-testid="testimonial-submit"
              className="flex-1 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
              {busy ? "Sending…" : "Submit"}
            </Button>
            <Button onClick={dismiss} variant="ghost" className="text-[#A1A1AA] rounded-full">Maybe later</Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
